/**
 * AuthContext — shared CHI Alliance account presentation state.
 *
 * Identity, roles, and sign-out are always sourced from the server-backed OAuth
 * session. Browser storage is limited to non-security profile presentation fields.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useAuth as useServerAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { hasFullSiteAccess } from "@shared/siteAccess";

export type UserRole = "attendee" | "admin";
export type MembershipTier = "free" | "premium";

export interface UserProfile {
  name: string;
  email: string;
  role: UserRole;
  membership: MembershipTier;
  title: string;
  company: string;
  bio: string;
  phone: string;
  linkedin: string;
  interests: string[];
  avatar: string;
}

type ProfileOverrides = Partial<
  Pick<
    UserProfile,
    "name" | "title" | "company" | "bio" | "phone" | "linkedin" | "interests" | "avatar"
  >
>;

interface AuthContextType {
  isLoggedIn: boolean;
  isPremium: boolean;
  user: UserProfile | null;
  logout: () => Promise<void>;
  /** Membership access is updated by the verified subscription workflow, never by local state. */
  upgradeToPremium: () => void;
  updateProfile: (updates: Partial<UserProfile>) => void;
}

const PROFILE_STORAGE_PREFIX = "chi_member_profile_v1:";
const AuthContext = createContext<AuthContextType | null>(null);

function getInitials(name: string, email: string) {
  const letters = name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0])
    .join("");

  return (letters || email.slice(0, 2) || "M").toUpperCase();
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const { user: secureUser, isAuthenticated, logout: secureLogout } = useServerAuth();
  const trpcUtils = trpc.useUtils();
  const entitlementQuery = trpc.members.myEntitlement.useQuery(undefined, {
    enabled: isAuthenticated,
    retry: false,
  });
  const [profileOverrides, setProfileOverrides] = useState<ProfileOverrides>({});

  useEffect(() => {
    if (!secureUser) {
      setProfileOverrides({});
      return;
    }

    try {
      const raw = localStorage.getItem(`${PROFILE_STORAGE_PREFIX}${secureUser.openId}`);
      setProfileOverrides(raw ? (JSON.parse(raw) as ProfileOverrides) : {});
    } catch {
      setProfileOverrides({});
    }
  }, [secureUser?.openId]);

  const user = useMemo<UserProfile | null>(() => {
    if (!secureUser) return null;

    const email = secureUser.email?.trim() ?? "";
    const name = profileOverrides.name?.trim() || secureUser.name?.trim() || email.split("@")[0] || "CHI Alliance Member";
    const role: UserRole = secureUser.role === "admin" ? "admin" : "attendee";

    return {
      name,
      email,
      role,
      membership: hasFullSiteAccess({ role, accessState: entitlementQuery.data?.accessState }) ? "premium" : "free",
      title: profileOverrides.title ?? "",
      company: profileOverrides.company ?? "",
      bio: profileOverrides.bio ?? "",
      phone: profileOverrides.phone ?? "",
      linkedin: profileOverrides.linkedin ?? "",
      interests: profileOverrides.interests ?? [],
      avatar: profileOverrides.avatar?.trim() || getInitials(name, email),
    };
  }, [entitlementQuery.data?.accessState, profileOverrides, secureUser]);

  const updateProfile = useCallback(
    (updates: Partial<UserProfile>) => {
      if (!secureUser) return;

      const permitted: ProfileOverrides = {};
      if (typeof updates.name === "string") permitted.name = updates.name.trim();
      if (typeof updates.title === "string") permitted.title = updates.title.trim();
      if (typeof updates.company === "string") permitted.company = updates.company.trim();
      if (typeof updates.bio === "string") permitted.bio = updates.bio.trim();
      if (typeof updates.phone === "string") permitted.phone = updates.phone.trim();
      if (typeof updates.linkedin === "string") permitted.linkedin = updates.linkedin.trim();
      if (typeof updates.avatar === "string") permitted.avatar = updates.avatar.trim();
      if (Array.isArray(updates.interests)) permitted.interests = updates.interests.filter(item => typeof item === "string");

      setProfileOverrides(previous => {
        const next = { ...previous, ...permitted };
        try {
          localStorage.setItem(`${PROFILE_STORAGE_PREFIX}${secureUser.openId}`, JSON.stringify(next));
        } catch {
          // A browser privacy setting can block storage without affecting authentication.
        }
        return next;
      });
    },
    [secureUser],
  );

  const logout = useCallback(async () => {
    const storageKey = secureUser ? `${PROFILE_STORAGE_PREFIX}${secureUser.openId}` : undefined;
    try {
      await secureLogout();
    } finally {
      if (storageKey) {
        try {
          localStorage.removeItem(storageKey);
        } catch {
          // Browser storage is not required for a secure sign-out.
        }
      }
      setProfileOverrides({});
    }
  }, [secureLogout, secureUser]);

  const value = useMemo<AuthContextType>(
    () => ({
      isLoggedIn: isAuthenticated,
      isPremium: hasFullSiteAccess({ role: user?.role, membership: user?.membership }),
      user,
      logout,
      // Local state must never grant paid access. This only refreshes server entitlement state after checkout returns.
      upgradeToPremium: () => {
        void trpcUtils.members.myEntitlement.invalidate();
      },
      updateProfile,
    }),
    [isAuthenticated, logout, trpcUtils.members.myEntitlement, updateProfile, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

/**
 * Auth state for components that must still render without a provider —
 * public marketing surfaces, and the same components under isolated tests.
 * Returns null instead of throwing; callers decide the signed-out default.
 */
export function useOptionalAuth() {
  return useContext(AuthContext);
}
